/**
 * MONSTER TOWN — WHICH KID IS PLAYING. HUD/AUDIO/TOUCH CREW (src/ui/**).
 *
 * Two save slots, one per kid, each with their own town (brief). This is the
 * picker, and it is the smallest screen in the game: two big cards, and you tap
 * the one that looks like YOURS.
 *
 * ============================ ROUND 2 REBUILD =============================
 * "Two nearly identical cards guarding the one thing the brief calls
 * unacceptable to lose." The critic was right, and every part of it:
 *
 *  - THE CARDS DID NOT SHOW WHOSE TOWN THEY WERE. Both slots drew a monster
 *    face on white with a coin count under it. Two kids whose monsters are both
 *    round and both smiling get two identical cards, and the only thing telling
 *    them apart was a number a pre-reader cannot read. Each card now takes its
 *    OWN monster's body colour as its background and its ring, so the two cards
 *    are two different colours before any shape is looked at.
 *  - THERE WAS NO TOWN ON THE "WHICH TOWN" SCREEN. Now there is: a little
 *    top-down map of that slot's actual placements, drawn from the save data
 *    itself (`townMap()`), so a kid recognises their own town by its shape — the
 *    one with the pond, the one with the long road. Nothing is stored for this;
 *    it is derived, so it can never go stale or bloat a save.
 *  - THE EMPTY SLOT SAID NOTHING. A blank cream face with a sparkle reads as
 *    "broken", not as "start here". It is now a dashed plot with a sprout and a
 *    plus in it — an invitation with an obvious shape.
 *  - THE STATS WERE A COIN AND A BROWN CUBE, and the cube is the build drawer's
 *    "a block you can place" glyph doing a completely different job. It is now
 *    `builtTown` — a little cluster of roofs, which is what the number counts.
 *  - THE CONFIRM WAS A DIFFERENT BUTTON FROM EVERY OTHER CONFIRM: an orange
 *    square holding a green tick-in-a-circle here, a plain green square with a
 *    white tick in the monster creator. Same question, two pictures. Every
 *    confirm in the app is now `.mt-btn.go` + the `tick` icon.
 *  - THE LAYOUT STRANDED THE CONFIRM at the bottom of ~60% empty cream. The
 *    cards are centred in the body and the foot sits under them.
 * ==========================================================================
 *
 * SAVES ARE SACRED — WHAT THIS SCREEN DOES NOT HAVE:
 *  - No erase button. None. A 3-year-old cannot lose their brother's town by
 *    tapping the wrong picture, because the tap that would do it does not exist.
 *    Erasing lives in the harness (`__harness.eraseSlot`) and nowhere a kid can
 *    reach it.
 *  - No confirm dialog either, because switching is not destructive:
 *    `save.useSlot()` COMMITS the current slot before it loads the other one.
 *
 * The card previews are read through `save.exportSlot()` and parsed defensively:
 * a corrupt or half-written slot renders as a friendly empty card, never as a
 * crash and never as a wrong monster.
 */

import { bus } from '../core/events'
import { save } from '../core/save'
import { css, type PaletteName } from '../core/palette'
import { DEFAULT_MONSTER, type MonsterConfig, type SaveSlot } from '../core/types'
import { button, el, onTap } from './dom'
import { iconEl } from './icons'
import { monsterPortrait } from './monsterArt'
import { panelShell, type PanelHost, type PanelParts } from './panels'

const SLOTS: readonly SaveSlot[] = ['a', 'b']

/** One dot on the little map. */
interface MapDot {
  x: number
  z: number
  kind: string
}

interface SlotPreview {
  exists: boolean
  coins: number
  look: MonsterConfig
  pieces: number
  dots: MapDot[]
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function str<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback
}

/**
 * WHAT COLOUR IS A THING ON THE MAP. Keyed off the id, because the slot picker
 * reads raw save text and must not depend on the registry having the def (a
 * save written by a later build can name pieces this build has never heard of —
 * they still draw, as a neutral dot, rather than taking the card down).
 * DATA: first match wins.
 */
const MAP_COLORS: readonly (readonly [string, PaletteName])[] = [
  ['path', 'path'],
  ['road', 'road'],
  ['water', 'water'],
  ['pond', 'water'],
  ['tree', 'leaf'],
  ['plant', 'leafLight'],
  ['flower', 'flowerPink'],
  ['fence', 'woodLight'],
  ['roof', 'roofRed'],
  ['wall', 'wallCream'],
  ['floor', 'floorWood'],
  ['house', 'roofRed'],
  ['shop', 'roofTeal'],
  ['venue', 'roofTeal'],
  ['town', 'metal'],
]

function dotColor(kind: string): string {
  for (const [frag, name] of MAP_COLORS) {
    if (kind.includes(frag)) return css(name)
  }
  return css('woodLight')
}

/** Parse a slot's raw save text into something drawable. Never throws. */
export function previewSlot(slot: SaveSlot): SlotPreview {
  const base: SlotPreview = {
    exists: false,
    coins: 0,
    look: { ...DEFAULT_MONSTER },
    pieces: 0,
    dots: [],
  }
  const peeked = save.peek(slot)
  base.exists = peeked.exists
  const text = save.exportSlot(slot)
  if (text === null) return base
  try {
    const raw: unknown = JSON.parse(text)
    if (!isRecord(raw) || !isRecord(raw['sections'])) return base
    const sections = raw['sections']

    const wallet = sections['wallet']
    if (isRecord(wallet) && isRecord(wallet['data'])) {
      const coins = wallet['data']['coins']
      if (typeof coins === 'number' && Number.isFinite(coins)) base.coins = Math.max(0, Math.floor(coins))
    }

    const monster = sections['monster']
    if (isRecord(monster) && isRecord(monster['data'])) {
      const look = monster['data']['look']
      if (isRecord(look)) {
        const l = base.look
        if (typeof look['color'] === 'string') l.color = look['color'] as PaletteName
        if (typeof look['accentColor'] === 'string') l.accentColor = look['accentColor'] as PaletteName
        l.shape = str(look['shape'], ['blob', 'tall', 'round', 'chunky'], l.shape)
        l.eyes = str(look['eyes'], ['two', 'one', 'three', 'sleepy'], l.eyes)
        l.horns = str(look['horns'], ['none', 'nubs', 'curved', 'antlers', 'antennae'], l.horns)
        l.pattern = str(look['pattern'], ['plain', 'spots', 'stripes', 'belly', 'star'], l.pattern)
      }
    }

    const placements = sections['placements']
    if (isRecord(placements) && isRecord(placements['data'])) {
      const scenes = placements['data']['scenes']
      if (isRecord(scenes)) {
        // The map is the OUTSIDE — that is the town a kid recognises. Interiors
        // still count toward "how much you built", they just are not on the map.
        let best: unknown[] = []
        for (const [sceneId, list] of Object.entries(scenes)) {
          if (!Array.isArray(list)) continue
          base.pieces += list.length
          if (sceneId.startsWith('town') && list.length > best.length) best = list
        }
        if (best.length === 0) {
          for (const list of Object.values(scenes)) {
            if (Array.isArray(list) && list.length > best.length) best = list
          }
        }
        for (const row of best) {
          if (!isRecord(row)) continue
          const x = row['x']
          const z = row['z']
          const d = row['d']
          if (typeof x !== 'number' || typeof z !== 'number' || typeof d !== 'string') continue
          base.dots.push({ x, z, kind: d })
        }
      }
    }
  } catch {
    /* a corrupt slot draws as an empty card — never as a crash */
  }
  return base
}

/**
 * THIS SLOT'S TOWN, FROM ABOVE. Derived from the save's own placement grid, so
 * two kids' towns look different the moment they have built anything different.
 * Cheap DOM canvas, no WebGL, no stored thumbnail to go stale.
 */
function townMap(p: SlotPreview, size: number): HTMLCanvasElement {
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  const cv = document.createElement('canvas')
  cv.width = Math.round(size * dpr)
  cv.height = Math.round(size * dpr)
  cv.style.width = `${size}px`
  cv.style.height = `${size}px`
  cv.style.display = 'block'
  cv.style.borderRadius = '18px'
  const c = cv.getContext('2d')
  if (c === null) return cv
  c.scale(dpr, dpr)

  c.fillStyle = css('grass')
  c.fillRect(0, 0, size, size)

  if (p.dots.length === 0) return cv

  let minX = Infinity
  let maxX = -Infinity
  let minZ = Infinity
  let maxZ = -Infinity
  for (const d of p.dots) {
    if (d.x < minX) minX = d.x
    if (d.x > maxX) maxX = d.x
    if (d.z < minZ) minZ = d.z
    if (d.z > maxZ) maxZ = d.z
  }
  const span = Math.max(6, maxX - minX + 1, maxZ - minZ + 1)
  const pad = size * 0.08
  const cell = (size - pad * 2) / span
  const offX = pad + ((span - (maxX - minX + 1)) / 2) * cell
  const offZ = pad + ((span - (maxZ - minZ + 1)) / 2) * cell
  const s = Math.max(2, cell * 0.92)
  for (const d of p.dots) {
    c.fillStyle = dotColor(d.kind)
    c.fillRect(offX + (d.x - minX) * cell, offZ + (d.z - minZ) * cell, s, s)
  }
  return cv
}

export function registerSlotPanel(host: PanelHost, onClose: () => void): void {
  host.register('slots', (): PanelParts => {
    const parts = panelShell({ icon: 'who', onBack: onClose })
    parts.root.classList.add('mt-slotscreen')
    const row = el('div', 'mt-slots')
    parts.body.append(row)

    const paint = (): void => {
      row.replaceChildren()
      for (const slot of SLOTS) {
        const p = previewSlot(slot)
        const card = el('button', 'mt-slot')
        card.setAttribute('aria-label', `Play in town ${slot.toUpperCase()}`)
        card.dataset['on'] = String(save.activeSlot === slot)
        card.dataset['empty'] = String(!p.exists)

        if (p.exists) {
          // THE CARD IS THAT MONSTER'S COLOUR. Two towns are two colours before
          // a single shape has to be told apart.
          card.style.setProperty('--slot-tint', css(p.look.color))
          card.style.setProperty('--slot-tint-2', css(p.look.accentColor))

          const face = el('div', 'mt-slot-face')
          face.append(monsterPortrait(p.look, 132))
          card.append(face)

          const map = el('div', 'mt-slot-map')
          map.append(townMap(p, 150))
          card.append(map)

          const stats = el('div', 'mt-slot-stats')
          const coins = el('div', 'mt-slot-coins')
          coins.append(iconEl('coin', 40))
          const n = el('span', 'mt-num')
          n.textContent = String(p.coins)
          coins.append(n)
          const built = el('div', 'mt-slot-coins')
          // NOT the drawer's `block` glyph: this counts what you have BUILT, not
          // a thing you can place.
          built.append(iconEl('builtTown', 40))
          const bn = el('span', 'mt-num')
          bn.textContent = String(p.pieces)
          built.append(bn)
          stats.append(coins, built)
          card.append(stats)
        } else {
          // An empty slot is an invitation, not an error state: a bare plot with
          // a sprout coming up and a big plus over it.
          const face = el('div', 'mt-slot-face mt-slot-new')
          face.append(iconEl('emptyPlot', 132))
          card.append(face)
          // A PLUS, not a hammer. The hammer already means build-or-play, and
          // reusing it here would be the same mis-promise the house icon on the
          // "which town" button was.
          const plus = el('div', 'mt-slot-plus')
          plus.append(iconEl('plus', 64))
          card.append(plus)
        }

        onTap(card, () => {
          if (save.activeSlot === slot) {
            onClose()
            return
          }
          // useSlot() commits the slot we are leaving BEFORE it loads the new
          // one. Nothing is ever lost by switching.
          save.useSlot(slot)
          bus.emit('ui:toast', { icon: 'tick', ms: 1400 })
          onClose()
        })
        row.append(card)
      }
    }

    // ONE CONFIRM PICTURE IN THE WHOLE APP: green button, white tick. See the
    // header note — this used to be an orange square holding a green
    // tick-in-a-circle while the monster creator's was a green square with a
    // white tick, for exactly the same question.
    parts.foot.append(
      button({ icon: 'tick', label: 'Keep playing here', className: 'big go' }, onClose),
    )

    paint()
    return parts
  })
}
